<template>
  <b-form-input
    type="text"
    inputmode="decimal"
    autocomplete="off"
    v-bind="$attrs"
    :value="display"
    v-on="forwardedListeners"
    @keydown="onKeydown"
    @input="onInput"
    @blur="onBlur"
    @focus="onFocus"
    @click="onClick"
  />
</template>

<script>
/**
 * Input de monto con formato es-AR en vivo mientras se tipea (miles con
 * punto, decimales con coma) — mismo v-model de siempre (número crudo), no
 * un string formateado. Reemplazo de `<b-form-input type="number">` en
 * inputs de montos EDITABLES (los de solo lectura ya se resuelven con un
 * computed + Intl.NumberFormat, ver docs/list-view-interfaces).
 *
 * Estrategia: edición convencional. Los dígitos se interpretan como parte
 * entera ("1234" muestra "1.234,00") y el usuario usa coma o punto sólo
 * cuando quiere cargar los decimales. Así no hace falta pensar en centavos
 * mientras se escribe un importe entero.
 */
export default {
  name: 'AppNumberInput',
  inheritAttrs: false,
  props: {
    value: { type: [Number, String], default: 0 },
    decimals: { type: Number, default: 2 },
    selectOnFocus: { type: Boolean, default: true },
    allowEmpty: { type: Boolean, default: false },
  },
  data() {
    return {
      cents: this.toCents(this.value),
      lastEmitted: this.toNumber(this.value),
      decimalMode: false,
      decimalDigits: '',
      integerDigits: '',
      cleared: this.allowEmpty && this.value === '',
      valueOnFocus: null,
    }
  },
  computed: {
    display() {
      if (this.cleared) {
        return ''
      }
      if (this.decimalMode) {
        return this.formatDecimalEditing()
      }
      return this.formatCents(this.cents)
    },
    // @input lo maneja este componente (necesita interceptarlo para
    // aplicar la máscara) y @change lo emite él mismo en el blur (ver
    // onBlur) — el resto de los listeners del padre se reenvían tal cual.
    forwardedListeners() {
      // eslint-disable-next-line no-unused-vars
      const { input, change, keydown, blur, focus, click, ...rest } = this.$listeners
      return rest
    },
  },
  watch: {
    value(newValue) {
      if(this.allowEmpty && newValue === '') {
        this.lastEmitted = 0
        this.cents = 0
        this.decimalMode = false
        this.decimalDigits = ''
        this.integerDigits = ''
        this.cleared = true
        return
      }
      const num = this.toNumber(newValue)
      // Si el cambio vino de nuestro propio $emit, no hay que resincronizar
      // (cortaría el tipeo del usuario a mitad de camino).
      if (num === this.lastEmitted) return
      // lastEmitted pasa a ser "último valor conocido": si el padre corrige
      // el monto (ej. tope), el próximo change se compara contra eso.
      this.lastEmitted = num
      this.cents = this.toCents(newValue)
      this.decimalMode = false
      this.decimalDigits = ''
      this.integerDigits = ''
      this.cleared = false
    },
  },
  methods: {
    toNumber(val) {
      return parseFloat(val) || 0
    },
    toCents(val) {
      return Math.round(this.toNumber(val) * Math.pow(10, this.decimals))
    },
    formatCents(cents) {
      const num = cents / Math.pow(10, this.decimals)
      return new Intl.NumberFormat('es-AR', {
        minimumFractionDigits: this.decimals,
        maximumFractionDigits: this.decimals,
      }).format(num)
    },
    formatInteger(value) {
      return new Intl.NumberFormat('es-AR', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(value)
    },
    formatDecimalEditing() {
      const factor = Math.pow(10, this.decimals)
      const integer = Math.trunc(this.cents / factor)
      return this.formatInteger(integer) + ',' + this.decimalDigits
    },
    emitNumber(num) {
      this.cents = this.toCents(num)
      this.lastEmitted = num
      this.$emit('input', num)
    },
    callListener(name, event) {
      const listener = this.$listeners[name]
      if(Array.isArray(listener)) {
        listener.forEach(fn => fn(event))
      } else if(typeof listener === 'function') {
        listener(event)
      }
    },
    setCursorEnd() {
      this.$nextTick(() => {
        const el = this.$el
        if (el && el.setSelectionRange) {
          el.value = this.display
          const len = el.value.length
          el.setSelectionRange(len, len)
        }
      })
    },
    hasFullSelection(event) {
      const el = event.target
      return el && el.selectionStart === 0 && el.selectionEnd === el.value.length && el.value.length > 0
    },
    isDecimalSeparator(key) {
      return key === ',' || key === '.'
    },
    clearInput() {
      this.cents = 0
      this.lastEmitted = 0
      this.decimalMode = false
      this.decimalDigits = ''
      this.integerDigits = ''
      this.cleared = true
      this.$emit('input', this.allowEmpty ? '' : 0)
      this.$nextTick(() => {
        if(this.$el) {
          this.$el.value = ''
        }
      })
    },
    startDecimalInput() {
      this.cents = 0
      this.decimalMode = true
      this.decimalDigits = ''
      this.integerDigits = ''
      this.cleared = false
      this.updateDecimalValue()
    },
    updateDecimalValue() {
      const factor = Math.pow(10, this.decimals)
      const integer = Math.trunc(this.cents / factor)
      const decimalText = (this.decimalDigits + '0'.repeat(this.decimals)).slice(0, this.decimals)
      const num = integer + ((parseInt(decimalText, 10) || 0) / factor)
      this.emitNumber(num)
      this.setCursorEnd()
    },
    startIntegerInput(digit) {
      this.cents = 0
      this.decimalMode = false
      this.decimalDigits = ''
      this.integerDigits = digit
      this.cleared = false
      this.emitNumber(parseInt(this.integerDigits, 10))
      this.setCursorEnd()
    },
    appendIntegerDigit(digit) {
      const factor = Math.pow(10, this.decimals)
      const currentInteger = this.integerDigits || String(Math.trunc(this.cents / factor))
      this.integerDigits = (currentInteger === '0' ? '' : currentInteger) + digit
      this.cleared = false
      this.emitNumber(parseInt(this.integerDigits || '0', 10))
      this.setCursorEnd()
    },
    removeIntegerDigit() {
      const factor = Math.pow(10, this.decimals)
      const currentInteger = this.integerDigits || String(Math.trunc(this.cents / factor))
      this.integerDigits = currentInteger.slice(0, -1)
      this.cleared = this.integerDigits === ''
      this.emitNumber(parseInt(this.integerDigits || '0', 10))
      this.setCursorEnd()
    },
    parseDisplayValue(text) {
      const raw = String(text || '').replace(/\s/g, '')
      const commaIndex = raw.lastIndexOf(',')
      const dotIndexes = raw.match(/\./g) || []
      // En formato es-AR el punto suele ser miles. Si llega un único punto
      // con hasta la cantidad permitida de dígitos a la derecha, también se
      // acepta como decimal (teclado numérico/paste "1234.50").
      const dotIndex = raw.lastIndexOf('.')
      const dotIsDecimal = this.decimals > 0 && commaIndex === -1 && dotIndexes.length === 1 &&
        dotIndex >= 0 && raw.length - dotIndex - 1 <= this.decimals
      const separatorIndex = commaIndex >= 0 ? commaIndex : (dotIsDecimal ? dotIndex : -1)
      const integerText = separatorIndex >= 0 ? raw.slice(0, separatorIndex) : raw
      const decimalText = separatorIndex >= 0 ? raw.slice(separatorIndex + 1) : ''
      const integerDigits = integerText.replace(/[^0-9]/g, '')
      const decimalDigits = decimalText.replace(/[^0-9]/g, '').slice(0, this.decimals)
      const factor = Math.pow(10, this.decimals)
      const decimalValue = parseInt((decimalDigits + '0'.repeat(this.decimals)).slice(0, this.decimals), 10) || 0

      return {
        integerDigits,
        decimalDigits,
        hasDecimal: separatorIndex >= 0,
        value: (parseInt(integerDigits, 10) || 0) + (decimalValue / factor),
      }
    },
    onKeydown(event) {
      this.callListener('keydown', event)

      if(event.defaultPrevented) {
        return
      }

      if(this.hasFullSelection(event)) {
        if(event.key === 'Backspace' || event.key === 'Delete') {
          event.preventDefault()
          this.clearInput()
          return
        }

        if(/^[0-9]$/.test(event.key)) {
          event.preventDefault()
          this.startIntegerInput(event.key)
          return
        }

        if(this.isDecimalSeparator(event.key) && this.decimals > 0) {
          event.preventDefault()
          this.startDecimalInput()
          return
        }
      }

      if(this.isDecimalSeparator(event.key) && this.decimals > 0) {
        event.preventDefault()
        const factor = Math.pow(10, this.decimals)
        const integer = this.integerDigits ? parseInt(this.integerDigits, 10) : Math.trunc(this.cents / factor)
        this.cents = integer * factor
        this.decimalMode = true
        this.decimalDigits = ''
        this.updateDecimalValue()
        return
      }

      if(!this.decimalMode) {
        if(/^[0-9]$/.test(event.key)) {
          event.preventDefault()
          this.appendIntegerDigit(event.key)
          return
        }

        if(event.key === 'Backspace') {
          event.preventDefault()
          this.removeIntegerDigit()
          return
        }

        if(event.key === 'Delete') {
          event.preventDefault()
        }
        return
      }

      if(/^[0-9]$/.test(event.key)) {
        event.preventDefault()
        if(this.decimalDigits.length < this.decimals) {
          this.decimalDigits += event.key
          this.updateDecimalValue()
        }
        return
      }

      if(event.key === 'Backspace') {
        event.preventDefault()
        if(this.decimalDigits.length) {
          this.decimalDigits = this.decimalDigits.slice(0, -1)
          this.updateDecimalValue()
        } else {
          this.decimalMode = false
          this.setCursorEnd()
        }
        return
      }

      if(event.key === 'Delete') {
        event.preventDefault()
      }
    },
    onInput(text) {
      if(!text) {
        this.clearInput()
        return
      }

      this.cleared = false

      // Este camino atiende pegado, autocompletado y teclados móviles que no
      // entregan keydown. La escritura normal se resuelve en onKeydown.
      const parsed = this.parseDisplayValue(text)
      this.integerDigits = parsed.integerDigits
      this.decimalDigits = parsed.decimalDigits
      this.decimalMode = parsed.hasDecimal
      this.emitNumber(parsed.value)
      this.setCursorEnd()
    },
    onBlur(event) {
      this.decimalMode = false
      this.decimalDigits = ''
      this.integerDigits = ''
      this.cleared = this.allowEmpty && !this.$el.value
      if(!this.$el.value) {
        if(!this.allowEmpty) {
          this.emitNumber(0)
          this.setCursorEnd()
        }
      }
      // El tipeo se resuelve con preventDefault + valor escrito por script,
      // así que el navegador nunca dispara el change nativo: se emite acá,
      // con la misma semántica (sólo si el valor cambió desde el focus) y
      // antes del blur, como el nativo.
      if(this.valueOnFocus !== null && this.lastEmitted !== this.valueOnFocus) {
        this.$emit('change', this.lastEmitted)
      }
      this.valueOnFocus = null
      this.callListener('blur', event)
    },
    onFocus(event) {
      this.valueOnFocus = this.lastEmitted
      this.callListener('focus', event)
      this.selectText(event.target)
    },
    onClick(event) {
      this.callListener('click', event)
      // El click puede reposicionar el cursor después de focus.
      this.$nextTick(() => this.selectText(event.target))
    },
    selectText(input) {
      if(!this.selectOnFocus || !input || typeof input.select !== 'function') {
        return
      }
      input.select()
    },
  },
}
</script>
